/**
 * The rules around editing a workspace artifact in the browser: which files can be edited at all, and the
 * two refusals the client owns so a doomed write never leaves the tab. The service stays the authority —
 * it answers 413 over the cap and sanitizes a name — this is only about not spending a round-trip on an
 * answer we already know.
 */

import { MAX_ARTIFACT_REVISION_BYTES } from '@/lib/api/workspace-artifacts.js';

import {
  resolvePreviewKind,
  type ArtifactPreviewKind,
} from './artifact-display.js';

/**
 * The preview kinds that are also safe to EDIT: the text-shaped ones. `markdown`, `table` (csv/tsv),
 * `text` (txt/json/log/yaml), `mermaid` (mmd) and `html` are all decoded to a string and saved back as one,
 * so a round-trip through the editor is lossless.
 *
 * Everything else — pdf, images (SVG included), office formats, zip archives, unknown binaries — stays
 * download-only. There is no safe
 * in-browser editing for them, and an editor that writes a mangled binary back over the original is worse
 * than no editor at all.
 */
const EDITABLE_PREVIEW_KINDS: ReadonlySet<ArtifactPreviewKind> = new Set([
  'markdown',
  'mermaid',
  'table',
  'text',
  'html',
]);

/** Whether this artifact's content can be edited in place, judged the same way the preview is chosen. */
export function isEditableArtifact(artifact: {
  contentType: string;
  name: string;
}): boolean {
  return EDITABLE_PREVIEW_KINDS.has(
    resolvePreviewKind(artifact.contentType, artifact.name),
  );
}

/** The cap as the user reads it, derived from the one constant so the two can never disagree. */
const LIMIT_LABEL = `${MAX_ARTIFACT_REVISION_BYTES / (1024 * 1024)} MB`;

/**
 * UTF-8 byte length of the edited text — what the upload actually costs. A character count would lie about
 * anything non-ASCII and let an over-cap save through to a 413.
 */
export function textByteSize(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** Why this text cannot be saved, or null when it fits. */
export function textSizeRejection(text: string): string | null {
  if (textByteSize(text) <= MAX_ARTIFACT_REVISION_BYTES) return null;
  return `These changes are over the ${LIMIT_LABEL} limit. Remove some text and try again.`;
}

/** Why this file cannot be uploaded, or null when it fits. */
export function artifactUploadRejection(file: {
  name: string;
  size: number;
}): string | null {
  if (file.size <= MAX_ARTIFACT_REVISION_BYTES) return null;
  return `“${file.name}” is over the ${LIMIT_LABEL} limit.`;
}

/**
 * Why this new name cannot be sent, or null when it is usable. Only emptiness is checked here: the service
 * sanitizes the rest of the name and answers with what it stored, so second-guessing its rules in the
 * client would only invent refusals the server does not have.
 */
export function renameRejection(name: string): string | null {
  if (name.trim().length === 0) return 'Enter a file name.';
  return null;
}
